/**
 * Ops V2 API client. All requests send X-Org-Id from org context.
 * Handles 401/403/404/422 with UI-friendly errors.
 */

import { getApiBase, getAuthHeaders, clearAccessToken } from "../auth";
import { getCurrentOrgId } from "../../state/orgContext";
import type {
  ProcessTemplateOut,
  ProcessTemplateFullDetailStudio,
  ProcessVersionOut,
} from "./types";

const API_BASE = getApiBase();
const OPS_V2_PREFIX = `${API_BASE}/ops/v2`;

export type OpsV2Error = Error & {
  status?: number;
  code?: string;
  correlationId?: string;
  validationErrors?: Array<{ loc?: string[]; msg?: string }>;
};

async function parseErrorResponse(response: Response): Promise<{ message: string; detail?: unknown; correlationId?: string }> {
  const correlationId = response.headers.get("X-Correlation-Id") ?? undefined;
  const text = await response.text();
  let message = "Erreur inconnue";
  let detail: unknown;
  try {
    const json = JSON.parse(text);
    detail = json.detail;
    if (typeof json.detail === "string" && json.detail.trim()) {
      message = json.detail;
    } else if (Array.isArray(json.detail) && json.detail.length > 0) {
      const parts = json.detail.map((d: { loc?: string[]; msg?: string }) => {
        const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
        const msg = typeof d.msg === "string" ? d.msg : "Valeur invalide";
        return loc ? `${loc}: ${msg}` : msg;
      });
      message = parts.join(" | ");
    }
  } catch {
    if (text.trim()) message = text;
  }
  return { message, detail, correlationId };
}

/**
 * Perform a request to /api/ops/v2. Adds X-Org-Id from org context.
 * 401: throws (caller/App should redirect to login).
 * 403: throws with message "Permission manquante" or backend detail.
 * 404: throws with message "Introuvable ou non accessible".
 * 422: throws with validation message and optional validationErrors.
 */
export async function opsV2Request<T>(
  path: string,
  options: {
    method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
    body?: unknown;
    orgId?: string | null;
  } = {}
): Promise<T> {
  const orgId = options.orgId ?? getCurrentOrgId();
  if (!orgId) {
    const err = new Error("Aucune organisation sélectionnée. Choisissez une organisation dans le sélecteur.") as OpsV2Error;
    err.status = 400;
    throw err;
  }

  const url = path.startsWith("http") ? path : `${OPS_V2_PREFIX}${path.startsWith("/") ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    ...getAuthHeaders({ orgId, json: true }),
  };

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
  };
  if (options.body !== undefined && options.method !== "GET") {
    init.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, init);

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  const { message, detail, correlationId } = await parseErrorResponse(response);
  const err = new Error(message) as OpsV2Error;
  err.status = response.status;
  err.correlationId = correlationId;

  if (response.status === 401) {
    clearAccessToken();
    err.code = "unauthorized";
    throw err;
  }

  if (response.status === 403) {
    err.code = "forbidden";
    if (!message || message === "Erreur inconnue") {
      (err as Error).message = "Vous n'avez pas les droits pour cette action. Demandez à un administrateur de vérifier vos permissions.";
    }
    throw err;
  }

  if (response.status === 404) {
    err.code = "not_found";
    (err as Error).message = "Introuvable ou non accessible.";
    throw err;
  }

  if (response.status === 422 && Array.isArray(detail)) {
    err.validationErrors = detail as Array<{ loc?: string[]; msg?: string }>;
    throw err;
  }

  throw err;
}

/** Create a new draft rule set from a published one. Returns the new draft. */
export async function createDraftFromPublished(
  ruleSetId: string,
  orgId?: string | null
): Promise<{ id: string; product_id: string; status: string; version_no: number }> {
  return opsV2Request<{ id: string; product_id: string; status: string; version_no: number }>(
    `/rule-sets/${ruleSetId}/create-draft`,
    { method: "POST", orgId: orgId ?? getCurrentOrgId() }
  );
}

/** Publish a draft rule set. Throws with detail (validation errors) on 400. */
export async function publishRuleSet(
  ruleSetId: string,
  body?: { correlation_id?: string | null },
  orgId?: string | null
): Promise<unknown> {
  return opsV2Request(`/rule-sets/${ruleSetId}/publish`, {
    method: "POST",
    body: body ?? {},
    orgId: orgId ?? getCurrentOrgId(),
  });
}

/** Set this published rule set as current for the product (rollback). */
export async function setCurrentRuleSet(
  ruleSetId: string,
  body?: { correlation_id?: string | null },
  orgId?: string | null
): Promise<unknown> {
  return opsV2Request(`/rule-sets/${ruleSetId}/set-current`, {
    method: "POST",
    body: body ?? {},
    orgId: orgId ?? getCurrentOrgId(),
  });
}

/** Get diff between two rule sets (only_in_a, only_in_b, changed). */
export async function getRuleSetDiff(
  ruleSetIdA: string,
  ruleSetIdB: string,
  orgId?: string | null
): Promise<{ only_in_a: string[]; only_in_b: string[]; changed: Array<{ rule_key: string; in_a: Record<string, unknown>; in_b: Record<string, unknown> }> }> {
  return opsV2Request(
    `/rule-sets/${ruleSetIdA}/diff/${ruleSetIdB}`,
    { orgId: orgId ?? getCurrentOrgId() }
  );
}

/** Delete a rule from a draft rule set. */
export async function deleteRule(
  ruleSetId: string,
  ruleId: string,
  orgId?: string | null
): Promise<void> {
  return opsV2Request(`/rule-sets/${ruleSetId}/rules/${ruleId}`, {
    method: "DELETE",
    orgId: orgId ?? getCurrentOrgId(),
  });
}

// --- Studio: process templates ---

export type ProcessTemplateCreateBody = { name: string; ops_family?: string | null };
export type ProcessTemplateUpdateBody = { name?: string | null; ops_family?: string | null; status?: string | null };
export type ProcessTemplatePublishBody = { version_number?: number | null; schema_version?: string | null };

export async function createProcessTemplateStudio(
  body: ProcessTemplateCreateBody,
  orgId?: string | null
): Promise<ProcessTemplateOut> {
  return opsV2Request<ProcessTemplateOut>("/process-templates", {
    method: "POST",
    body,
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function getProcessTemplateFullStudio(
  templateId: string,
  orgId?: string | null
): Promise<ProcessTemplateFullDetailStudio> {
  return opsV2Request<ProcessTemplateFullDetailStudio>(`/process-templates/${templateId}/full`, {
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function updateProcessTemplateStudio(
  templateId: string,
  body: ProcessTemplateUpdateBody,
  orgId?: string | null
): Promise<ProcessTemplateOut> {
  return opsV2Request<ProcessTemplateOut>(`/process-templates/${templateId}`, {
    method: "PATCH",
    body,
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function publishProcessTemplateStudio(
  templateId: string,
  body?: ProcessTemplatePublishBody | null,
  orgId?: string | null
): Promise<ProcessVersionOut> {
  return opsV2Request<ProcessVersionOut>(`/process-templates/${templateId}/publish`, {
    method: "POST",
    body: body ?? {},
    orgId: orgId ?? getCurrentOrgId(),
  });
}

// Studio steps/activities (Option B: org-scoped, no admin context)
export type ProcessStepTemplateCreateBody = {
  order_index: number;
  step_key?: string | null;
  title: string;
  description?: string | null;
  sla_days?: number | null;
  completion_policy?: string;
};
export type ProcessStepTemplateUpdateBody = {
  order_index?: number;
  title?: string;
  description?: string | null;
  sla_days?: number | null;
  completion_policy?: string;
};

export async function studioAddStep(
  templateId: string,
  body: ProcessStepTemplateCreateBody,
  orgId?: string | null
): Promise<{ id: string; order_index: number; title: string; [key: string]: unknown }> {
  return opsV2Request(`/process-templates/${templateId}/steps`, {
    method: "POST",
    body,
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function studioUpdateStep(
  templateId: string,
  stepId: string,
  body: ProcessStepTemplateUpdateBody,
  orgId?: string | null
): Promise<{ id: string; [key: string]: unknown }> {
  return opsV2Request(`/process-templates/${templateId}/steps/${stepId}`, {
    method: "PATCH",
    body,
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function studioDeleteStep(
  templateId: string,
  stepId: string,
  orgId?: string | null
): Promise<void> {
  return opsV2Request(`/process-templates/${templateId}/steps/${stepId}`, {
    method: "DELETE",
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function studioReorderStep(
  templateId: string,
  stepId: string,
  newOrderIndex: number,
  orgId?: string | null
): Promise<{ id: string; [key: string]: unknown }> {
  return opsV2Request(`/process-templates/${templateId}/steps/${stepId}/reorder`, {
    method: "POST",
    body: { new_order_index: newOrderIndex },
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export type ProcessActivityTemplateCreateBody = {
  title: string;
  activity_type: string;
  is_required?: boolean;
  description?: string | null;
  activity_ui_config?: Record<string, unknown> | null;
  checklist_config?: unknown;
  cost_activity_config?: unknown;
  assignee_activity_config?: unknown;
};

export async function studioAddActivity(
  templateId: string,
  stepId: string,
  body: ProcessActivityTemplateCreateBody,
  orgId?: string | null
): Promise<{ id: string; [key: string]: unknown }> {
  return opsV2Request(`/process-templates/${templateId}/steps/${stepId}/activities`, {
    method: "POST",
    body,
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export type ProcessActivityTemplateUpdateBody = {
  title?: string;
  description?: string | null;
  is_required?: boolean;
  activity_type?: string;
  sla_hours?: number | null;
  assignee_type?: string | null;
  assignee_role?: string | null;
  activity_ui_config?: Record<string, unknown> | null;
  checklist_config?: unknown;
  cost_activity_config?: unknown;
  assignee_activity_config?: unknown;
};

export async function studioUpdateActivity(
  templateId: string,
  activityId: string,
  body: ProcessActivityTemplateUpdateBody,
  orgId?: string | null
): Promise<{ id: string; [key: string]: unknown }> {
  return opsV2Request(`/process-templates/${templateId}/activities/${activityId}`, {
    method: "PATCH",
    body,
    orgId: orgId ?? getCurrentOrgId(),
  });
}

export async function studioDeleteActivity(
  templateId: string,
  activityId: string,
  orgId?: string | null
): Promise<void> {
  return opsV2Request(`/process-templates/${templateId}/activities/${activityId}`, {
    method: "DELETE",
    orgId: orgId ?? getCurrentOrgId(),
  });
}

